import type { CSSObject } from '@emotion/react';
import type { Theme } from '@mui/material';

export type FocusRingVariant = 'underline' | 'inside' | 'outside';

export interface FocusRingOptions {
  /**
   * 포커스링의 테두리 라운딩(반지름)을 지정합니다.
   *
   * @default 1
   */
  borderRadius?: number;
  /**
   * 포커스 링의 색상을 지정합니다. `variant`가 `underline`인 경우에는 항상 `false`로 표시됩니다.
   *
   * - `false`인 경우 바깥쪽이 어두운 색, 안쪽이 밝은 색으로 지정됩니다.
   * - `true`인 경우 바깥쪽이 밝은 색, 안쪽이 어두운 색으로 지정됩니다.
   *
   * @default false
   */
  inverse?: boolean;
  /**
   * 포커스 링의 위치를 조정합니다. (px)
   *
   * @default
   * - `underline`: 0
   * - `inside`: 1 * thickness
   * - `outside`: -1 * thickness
   */
  offset?: number;
  /**
   * 포커스 링을 표시할 요소를 지정합니다.
   */
  selectors?: {
    /**
     * 포커스 상태를 정의하는 CSS 선택자를 지정합니다. (ex. `&:focus-visible`, `&.${inputClasses.focused}`)
     *
     * @default "&:focus-visible"
     */
    focus?: string;
    /**
     * 포커스 링을 표시할 pseudo element를 지정합니다. 기본값으로 `&::before`를 사용하며, `&::before`를 이미 사용하고 있는 경우에 `&::after`를 사용할 수 있습니다.
     *
     * @default "&::before"
     */
    outline?: string;
  };
  /**
   * 포커스 링의 두께를 지정합니다.
   *
   * @default 2
   */
  thickness?: 1 | 2;
}
export const createFocusRing = (theme: Omit<Theme, 'mixins'>) =>
  /**
   * @param variant 포커스 링의 형태를 지정합니다.
   * - `underline`: 요소의 아래쪽에 표시됩니다. (ex. TextInput)
   * - `inside`: 요소의 안쪽에 표시됩니다. (ex. ListItem)
   * - `outside`: 요소의 바깥쪽에 표시됩니다. (ex. Button)
   * @param options 포커스 링의 옵션을 지정합니다.
   * @returns CSSObject
   */
  function focusRing(variant: FocusRingVariant, options?: FocusRingOptions): CSSObject {
    const {
      borderRadius = 1,
      inverse = false,
      offset: offsetOption,
      selectors,
      thickness = 2,
    } = options || {};
    const { focus = '&:focus-visible', outline = '&::before' } = selectors ?? {};

    const outerColor = inverse
      ? theme.color['focusRing/inverse']
      : theme.color['focusRing/default'];
    const innerColor = inverse
      ? theme.color['focusRing/default']
      : theme.color['focusRing/inverse'];
    const offset = offsetOption ?? (variant === 'inside' ? thickness : -thickness);

    switch (variant) {
      case 'underline':
        return {
          [focus]: {
            outline: 'none',
            [outline]: {
              borderBottom: `${thickness}px solid ${outerColor}`,
            },
          },
        };
      case 'inside':
      case 'outside':
        return {
          [focus]: {
            outline: 'none',
            [outline]: {
              content: '""',
              position: 'absolute',
              top: offset,
              bottom: offset,
              left: offset,
              right: offset,
              outline: `${thickness}px solid ${outerColor}`,
              border: `${thickness}px solid ${innerColor}`,
              borderRadius: borderRadius,
              pointerEvents: 'none',
            },
          },
        };
    }
  };
